import { Component, h } from 'preact'
import { Button } from 'ac-patterns'

const fullLightUrl = new URL('../xchart.com-for-light.svg', import.meta.url)
const fullDarkUrl = new URL('../xchart.com-for-dark.svg', import.meta.url)
const shortLightUrl = new URL('../xchart-for-light.svg', import.meta.url)
const shortDarkUrl = new URL('../xchart-for-dark.svg', import.meta.url)
const xLightUrl = new URL('../x-for-light.svg', import.meta.url)
const xDarkUrl = new URL('../x-for-dark.svg', import.meta.url)

const BACKGROUND_OPTIONS_BY_MODE = {
  light: [
    { value: 'transparent', label: 'Transparent' },
    { value: 'white', label: 'White' },
  ],
  dark: [
    { value: 'transparent', label: 'Transparent' },
    { value: 'dark-blue', label: 'Dark blue' },
  ],
}

const BACKGROUND_FILL = {
  'dark-blue': '#133651',
  white: '#FFFFFF',
}

const LOGO_OPTIONS = [
  { value: 'full', label: 'Xchart.com' },
  { value: 'short', label: 'Xchart' },
  { value: 'x', label: 'X' },
]

const MODE_OPTIONS = [
  { value: 'light', label: 'For light backgrounds' },
  { value: 'dark', label: 'For dark backgrounds' },
]

const FORMAT_OPTIONS = [
  { value: 'png', label: 'PNG' },
  { value: 'jpg', label: 'JPG' },
  { value: 'svg', label: 'SVG' },
]

const LOGO_SOURCES = {
  full: {
    light: fullLightUrl,
    dark: fullDarkUrl,
  },
  short: {
    light: shortLightUrl,
    dark: shortDarkUrl,
  },
  x: {
    light: xLightUrl,
    dark: xDarkUrl,
  },
}

const DEFAULT_WIDTH = 512

const checkerboardBackground = {
  backgroundColor: '#ffffff',
  backgroundImage:
    'linear-gradient(45deg, #e5edf2 25%, transparent 25%), linear-gradient(-45deg, #e5edf2 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5edf2 75%), linear-gradient(-45deg, transparent 75%, #e5edf2 75%)',
  backgroundPosition: '0 0, 0 12px, 12px -12px, -12px 0',
  backgroundSize: '24px 24px',
}

const formLayoutStyle = {
  gap: '1.5rem',
}

const columnStyle = {
  minWidth: '280px',
  flexBasis: '320px',
}

const fieldRowStyle = {
  margin: '0 -0.5rem',
}

const halfFieldStyle = {
  flex: '1 1 170px',
}

const previewImageStyle = {
  display: 'block',
}

const previewViewportStyle = {
  maxWidth: '100%',
  overflow: 'auto',
}

const parsePositiveInteger = value => {
  if (value === '' || value === null || typeof value === 'undefined') {
    return null
  }

  const parsed = parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }

  return parsed
}

const parseNonNegativeInteger = value => {
  if (value === '' || value === null || typeof value === 'undefined') {
    return 0
  }

  const parsed = parseInt(value, 10)
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0
  }

  return parsed
}

const parseViewBox = svgElement => {
  const viewBox = svgElement.getAttribute('viewBox')
  if (viewBox) {
    const parts = viewBox
      .split(/[\s,]+/)
      .map(Number)
      .filter(part => Number.isFinite(part))

    if (parts.length === 4) {
      return {
        minX: parts[0],
        minY: parts[1],
        width: parts[2],
        height: parts[3],
      }
    }
  }

  return {
    minX: 0,
    minY: 0,
    width: Number(svgElement.getAttribute('width')) || 1,
    height: Number(svgElement.getAttribute('height')) || 1,
  }
}

const parseSvgDefinition = svgText => {
  const parser = new window.DOMParser()
  const document = parser.parseFromString(svgText, 'image/svg+xml')
  const svgElement = document.documentElement
  const { minX, minY, width, height } = parseViewBox(svgElement)

  return {
    minX,
    minY,
    width,
    height,
    content: svgElement.innerHTML,
  }
}

const computeOutputDimensions = ({
  width,
  height,
  margin,
  baseWidth,
  baseHeight,
}) => {
  const safeMargin = Math.max(0, margin)
  const aspectRatio = baseWidth / baseHeight

  if (width && height) {
    return { width, height, margin: safeMargin }
  }

  if (width) {
    const derivedHeight = Math.max(
      1,
      Math.ceil((width - safeMargin * 2) / aspectRatio + safeMargin * 2)
    )

    return { width, height: derivedHeight, margin: safeMargin }
  }

  if (height) {
    const derivedWidth = Math.max(
      1,
      Math.ceil((height - safeMargin * 2) * aspectRatio + safeMargin * 2)
    )

    return { width: derivedWidth, height, margin: safeMargin }
  }

  const fallbackWidth = DEFAULT_WIDTH
  const fallbackHeight = Math.max(
    1,
    Math.ceil((fallbackWidth - safeMargin * 2) / aspectRatio + safeMargin * 2)
  )

  return {
    width: fallbackWidth,
    height: fallbackHeight,
    margin: safeMargin,
  }
}

const buildComposedSvg = ({
  definition,
  width,
  height,
  margin,
  background,
}) => {
  const availableWidth = Math.max(width - margin * 2, 1)
  const availableHeight = Math.max(height - margin * 2, 1)
  const scale = Math.min(
    availableWidth / definition.width,
    availableHeight / definition.height
  )
  const renderedWidth = definition.width * scale
  const renderedHeight = definition.height * scale
  const offsetX = (width - renderedWidth) / 2 - definition.minX * scale
  const offsetY = (height - renderedHeight) / 2 - definition.minY * scale
  const backgroundMarkup =
    background === 'transparent'
      ? ''
      : `<rect width="${width}" height="${height}" fill="${BACKGROUND_FILL[background]}" />`

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none">${backgroundMarkup}<g transform="translate(${offsetX} ${offsetY}) scale(${scale})">${definition.content}</g></svg>`
}

const svgToDataUrl = svgMarkup =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`

const canvasToBlob = (canvas, mimeType) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob)
        return
      }

      reject(new Error('Could not create the image file.'))
    }, mimeType)
  })

const loadImage = src =>
  new Promise((resolve, reject) => {
    const image = new window.Image()
    image.onload = () => resolve(image)
    image.onerror = () =>
      reject(new Error('Could not render the selected logo for download.'))
    image.src = src
  })

const downloadBlob = (blob, filename) => {
  const href = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  link.click()
  window.setTimeout(() => window.URL.revokeObjectURL(href), 1000)
}

const buildFilenamePart = value => value.replace(/[^a-z0-9]+/gi, '_')

const getBackgroundOptions = ({ mode, format }) =>
  BACKGROUND_OPTIONS_BY_MODE[mode].filter(
    option => format !== 'jpg' || option.value !== 'transparent'
  )

const buildFilename = ({
  logo,
  mode,
  background,
  margin,
  width,
  height,
  format,
}) =>
  `${buildFilenamePart(logo)}_${buildFilenamePart(mode)}_${buildFilenamePart(
    background
  )}_${margin}margin_${width}x${height}.${format}`

export class LogoApp extends Component {
  constructor(props) {
    super(props)
    this.state = {
      background: 'transparent',
      definitions: {},
      error: '',
      format: 'png',
      height: '',
      isDownloading: false,
      isLoading: true,
      logo: 'full',
      margin: '0',
      mode: 'light',
      previewUrl: '',
      width: '512',
    }
    this.previewRequestId = 0
  }

  componentDidMount() {
    this.loadDefinitions()
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.background !== this.state.background ||
      prevState.definitions !== this.state.definitions ||
      prevState.format !== this.state.format ||
      prevState.height !== this.state.height ||
      prevState.isLoading !== this.state.isLoading ||
      prevState.logo !== this.state.logo ||
      prevState.margin !== this.state.margin ||
      prevState.mode !== this.state.mode ||
      prevState.width !== this.state.width
    ) {
      this.updatePreview()
    }
  }

  async loadDefinitions() {
    try {
      const definitions = {}

      await Promise.all(
        Object.entries(LOGO_SOURCES).flatMap(([logoKey, modes]) =>
          Object.entries(modes).map(async ([modeKey, path]) => {
            const response = await fetch(path)
            const svgText = await response.text()

            if (!response.ok) {
              throw new Error(`Unable to load ${path}`)
            }

            if (!definitions[logoKey]) {
              definitions[logoKey] = {}
            }

            definitions[logoKey][modeKey] = parseSvgDefinition(svgText)
          })
        )
      )

      this.setState({
        definitions,
        isLoading: false,
      })
    } catch (error) {
      this.setState({
        error: error.message || 'Unable to load the logo files.',
        isLoading: false,
      })
    }
  }

  updateField(name, value) {
    const nextMode = name === 'mode' ? value : this.state.mode
    const nextFormat = name === 'format' ? value : this.state.format
    const backgroundOptions = getBackgroundOptions({
      mode: nextMode,
      format: nextFormat,
    })
    const requestedBackground =
      name === 'background' ? value : this.state.background
    const nextBackground = backgroundOptions.some(
      option => option.value === requestedBackground
    )
      ? requestedBackground
      : backgroundOptions[0].value
    const nextState = {
      [name]: value,
      background: nextBackground,
      error: '',
    }

    this.setState(nextState)
  }

  getCurrentDefinition() {
    const { definitions, logo, mode } = this.state
    return definitions[logo] && definitions[logo][mode]
  }

  getRenderState() {
    const definition = this.getCurrentDefinition()
    if (!definition) {
      return null
    }

    const width = parsePositiveInteger(this.state.width)
    const height = parsePositiveInteger(this.state.height)
    const margin = parseNonNegativeInteger(this.state.margin)
    const output = computeOutputDimensions({
      width,
      height,
      margin,
      baseWidth: definition.width,
      baseHeight: definition.height,
    })
    const svgMarkup = buildComposedSvg({
      definition,
      width: output.width,
      height: output.height,
      margin: output.margin,
      background: this.state.background,
    })

    return {
      ...output,
      definition,
      filename: buildFilename({
        logo: this.state.logo,
        mode: this.state.mode,
        background: this.state.background,
        margin: output.margin,
        width: output.width,
        height: output.height,
        format: this.state.format,
      }),
      svgMarkup,
    }
  }

  async updatePreview() {
    const renderState = this.getRenderState()
    if (!renderState) {
      if (this.state.previewUrl) {
        this.setState({ previewUrl: '' })
      }
      return
    }

    const requestId = ++this.previewRequestId
    const svgUrl = svgToDataUrl(renderState.svgMarkup)

    try {
      if (this.state.format === 'svg') {
        if (
          requestId === this.previewRequestId &&
          this.state.previewUrl !== svgUrl
        ) {
          this.setState({ previewUrl: svgUrl })
        }
        return
      }

      const image = await loadImage(svgUrl)
      const canvas = document.createElement('canvas')
      const pixelRatio = Math.max(1, window.devicePixelRatio || 1)
      canvas.width = Math.round(renderState.width * pixelRatio)
      canvas.height = Math.round(renderState.height * pixelRatio)
      const context = canvas.getContext('2d')

      if (!context) {
        throw new Error('Could not start the preview renderer.')
      }

      context.scale(pixelRatio, pixelRatio)
      context.drawImage(image, 0, 0, renderState.width, renderState.height)

      const mimeType = this.state.format === 'jpg' ? 'image/jpeg' : 'image/png'
      const previewUrl = canvas.toDataURL(mimeType)

      if (
        requestId === this.previewRequestId &&
        this.state.previewUrl !== previewUrl
      ) {
        this.setState({ previewUrl })
      }
    } catch (error) {
      if (requestId === this.previewRequestId) {
        this.setState({
          error: error.message || 'Could not render the preview.',
        })
      }
    }
  }

  async handleDownload() {
    const renderState = this.getRenderState()
    if (!renderState || this.state.isDownloading) {
      return
    }

    this.setState({
      error: '',
      isDownloading: true,
    })

    try {
      if (this.state.format === 'svg') {
        downloadBlob(
          new window.Blob([renderState.svgMarkup], { type: 'image/svg+xml' }),
          renderState.filename
        )
        return
      }

      const image = await loadImage(svgToDataUrl(renderState.svgMarkup))
      const canvas = document.createElement('canvas')
      canvas.width = renderState.width
      canvas.height = renderState.height
      const context = canvas.getContext('2d')

      if (!context) {
        throw new Error('Could not start the image renderer.')
      }

      if (
        this.state.format === 'jpg' &&
        this.state.background === 'transparent'
      ) {
        context.fillStyle = '#FFFFFF'
        context.fillRect(0, 0, canvas.width, canvas.height)
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height)

      const mimeType = this.state.format === 'jpg' ? 'image/jpeg' : 'image/png'
      const blob = await canvasToBlob(canvas, mimeType)
      downloadBlob(blob, renderState.filename)
    } catch (error) {
      this.setState({
        error: error.message || 'Could not download the logo.',
      })
    } finally {
      this.setState({
        isDownloading: false,
      })
    }
  }

  renderOptionGroup(label, name, options) {
    return (
      <label class='db mb3'>
        <div class='fw6 mb2'>{label}</div>
        <select
          class='input-reset ba b--black-20 br2 pa2 w-100 bg-white'
          value={this.state[name]}
          onInput={event => this.updateField(name, event.target.value)}
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    )
  }

  render() {
    const {
      background,
      error,
      format,
      height,
      isDownloading,
      isLoading,
      margin,
      previewUrl,
      width,
    } = this.state
    const renderState = this.getRenderState()
    const backgroundOptions = getBackgroundOptions({
      mode: this.state.mode,
      format,
    })
    const previewFrameStyle = renderState
      ? {
          width: `${renderState.width}px`,
          height: `${renderState.height}px`,
        }
      : null

    return (
      <div class='bg-dark-blue white flex justify-center pv4'>
        <div class='w-100 mw7 ph2 tl'>
          <h2 class='f2 mt0'>Logo Downloader</h2>
          <p class='measure-wide'>
            Pick the logo artwork, export format, background, margin, and final
            size. The logo is always centered and scaled without distortion.
          </p>

          {isLoading && <p>Loading logo files...</p>}

          {!isLoading && renderState && (
            <div class='flex flex-wrap items-start' style={formLayoutStyle}>
              <div class='flex-auto' style={columnStyle}>
                {this.renderOptionGroup('Logo', 'logo', LOGO_OPTIONS)}
                {this.renderOptionGroup(
                  'Background mode',
                  'mode',
                  MODE_OPTIONS
                )}
                {this.renderOptionGroup('Format', 'format', FORMAT_OPTIONS)}
                {this.renderOptionGroup(
                  'Background color',
                  'background',
                  backgroundOptions
                )}

                <div class='flex flex-wrap' style={fieldRowStyle}>
                  <label class='db mb3 ph2' style={halfFieldStyle}>
                    <div class='fw6 mb2'>Width (px)</div>
                    <input
                      class='input-reset ba b--black-20 br2 pa2 w-100'
                      inputMode='numeric'
                      min='1'
                      type='number'
                      value={width}
                      onInput={event =>
                        this.updateField('width', event.target.value)
                      }
                    />
                  </label>

                  <label class='db mb3 ph2' style={halfFieldStyle}>
                    <div class='fw6 mb2'>Height (px)</div>
                    <input
                      class='input-reset ba b--black-20 br2 pa2 w-100'
                      inputMode='numeric'
                      min='1'
                      placeholder='auto'
                      type='number'
                      value={height}
                      onInput={event =>
                        this.updateField('height', event.target.value)
                      }
                    />
                  </label>
                </div>

                <label class='db mb3'>
                  <div class='fw6 mb2'>Minimum margin (px)</div>
                  <input
                    class='input-reset ba b--black-20 br2 pa2 w-100'
                    inputMode='numeric'
                    min='0'
                    type='number'
                    value={margin}
                    onInput={event =>
                      this.updateField('margin', event.target.value)
                    }
                  />
                </label>

                <div class='pa3 br2 ba b--black-10 bg-white mb3'>
                  <div class='fw6 mb2'>Output</div>
                  <div class='mb1'>
                    Final size: {renderState.width} x {renderState.height}
                  </div>
                  <div class='mb1'>Background: {background}</div>
                  <div class='truncate'>Filename: {renderState.filename}</div>
                  {format === 'jpg' && (
                    <div class='mt2 f6 mid-gray'>
                      JPG exports always use an opaque background.
                    </div>
                  )}
                </div>

                <Button
                  disabled={isDownloading}
                  onClick={() => this.handleDownload()}
                  type='button'
                >
                  {isDownloading ? 'Preparing Download...' : 'Download Logo'}
                </Button>

                {error && <p class='dark-red mt3 mb0'>{error}</p>}
              </div>

              <div class='flex-auto' style={columnStyle}>
                <h3 class='mt0 mb2'>
                  Preview{' '}
                  <span className='f5 fw2 karla'>
                    (scroll side-to-side if needed)
                  </span>
                </h3>

                <div style={previewViewportStyle}>
                  <div
                    class='ba b--black-10 br3 overflow-hidden flex items-center justify-center'
                    style={
                      background === 'transparent'
                        ? {
                            ...checkerboardBackground,
                            ...previewFrameStyle,
                          }
                        : previewFrameStyle
                    }
                  >
                    {previewUrl && (
                      <img
                        alt='Generated logo preview'
                        class='db'
                        height={renderState.height}
                        src={previewUrl}
                        style={previewImageStyle}
                        width={renderState.width}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isLoading && !renderState && !error && (
            <p>Pick a logo option to begin.</p>
          )}

          {!isLoading && error && !renderState && (
            <p class='dark-red mb0'>{error}</p>
          )}
        </div>
      </div>
    )
  }
}
